import { useEffect, useMemo, useState } from "react";
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { AlertTriangleIcon, InfoIcon, RefreshCwIcon } from "lucide-react";
import { InfoHint } from "@dolfin/ui";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { formatDateTime, formatNumber } from "@/lib/format";
import {
  MATCH_OUTCOME_GROUPS,
  MATCH_SURVIVORSHIP_NOTE,
  MATCH_VIZ_CLASS,
  MATCH_VIZ_STYLES,
  groupOfBucket,
  toChartNumber,
  type MatchOutcomeBucketKey,
  type MatchOutcomeGroupKey,
} from "./matchOutcomeBuckets";
import { chartTooltipProps } from "@/components/charts/ChartTooltip";

type MatchGroupTimingRow = components["schemas"]["MatchGroupTimingRow"];

const ENDPOINT = "/admin/analytics/match-group-timing";

/** Below this many matched groups a median isn't a typical time, so the section says so in words. */
const MIN_GROUPS_FOR_MEDIAN = 10;

/** Everything - dates included - comes from the page filter, the same as every other section. */
function buildQuery(apiParams: URLSearchParams) {
  return {
    From: apiParams.get("From") ?? "",
    To: apiParams.get("To") ?? "",
    ClientIds: apiParams.getAll("ClientIds"),
    OrganisationId: apiParams.get("OrganisationId") || undefined,
  };
}

/** Nullable and maybe a string. Null (no matches yet) must stay null, not 0 as toChartNumber would make it. */
function toNullableSeconds(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  const num = typeof value === "string" ? Number(value) : value;
  return Number.isFinite(num) ? num : null;
}

/** Shortest readable duration: `4s`, `12m`, `5h 27m`, `3d 4h`; a zero smaller unit is dropped. */
export function formatGap(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  if (total < 60) return `${total}s`;

  const minutes = Math.floor(total / 60);
  if (minutes < 60) return `${minutes}m`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    const remainderMinutes = minutes % 60;
    return remainderMinutes === 0 ? `${hours}h` : `${hours}h ${remainderMinutes}m`;
  }

  const days = Math.floor(hours / 24);
  const remainderHours = hours % 24;
  return remainderHours === 0 ? `${days}d` : `${days}d ${remainderHours}h`;
}

/** The same gap spelled out for prose (`5 hours and 27 minutes`), where an abbreviation reads badly. */
export function formatGapWords(seconds: number): string {
  const unit = (value: number, name: string) => `${formatNumber(value)} ${plural(value, name, `${name}s`)}`;
  const total = Math.max(0, Math.round(seconds));

  if (total < 60) return unit(total, "second");

  const minutes = Math.floor(total / 60);
  if (minutes < 60) return unit(minutes, "minute");

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    const remainderMinutes = minutes % 60;
    return remainderMinutes === 0
      ? unit(hours, "hour")
      : `${unit(hours, "hour")} and ${unit(remainderMinutes, "minute")}`;
  }

  const days = Math.floor(hours / 24);
  const remainderHours = hours % 24;
  return remainderHours === 0
    ? unit(days, "day")
    : `${unit(days, "day")} and ${unit(remainderHours, "hour")}`;
}

/** The one phrase used everywhere a group has not been matched. Never a 0, never a blank cell. */
const NO_MATCH_YET = "No match yet";

/** Median of a non-empty list of gaps. Even counts average the two middle values. */
export function medianOf(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 1) return sorted[middle]!;
  return (sorted[middle - 1]! + sorted[middle]!) / 2;
}

function plural(count: number, singular: string, pluralForm: string): string {
  return count === 1 ? singular : pluralForm;
}

/** The five duration buckets plus "No match yet". Bounds in seconds, upper-exclusive, so each gap lands once. */
interface TimingBucketSpec {
  key: string;
  label: string;
  /** Inclusive lower bound in seconds. */
  from: number;
  /** Exclusive upper bound in seconds, or null for the open-ended final bucket. */
  to: number | null;
}

const TIMING_BUCKETS: TimingBucketSpec[] = [
  { key: "under-1m", label: "Under 1 minute", from: 0, to: 60 },
  { key: "1-10m", label: "1 to 10 minutes", from: 60, to: 600 },
  { key: "10-60m", label: "10 to 60 minutes", from: 600, to: 3600 },
  { key: "1-24h", label: "1 to 24 hours", from: 3600, to: 86400 },
  { key: "over-1d", label: "Over a day", from: 86400, to: null },
];

function bucketOfGap(seconds: number): TimingBucketSpec {
  return TIMING_BUCKETS.find((b) => seconds >= b.from && (b.to === null || seconds < b.to)) ?? TIMING_BUCKETS[4]!;
}

interface ChartPoint {
  key: string;
  label: string;
  groups: number;
  /** The "No match yet" bar is not a duration, so it is coloured apart from the five that are. */
  isNoMatch: boolean;
}

/** The three-category name for the method of a group's first match. */
function firstMethodLabel(bucket: MatchOutcomeBucketKey | null | undefined): string {
  if (!bucket) return NO_MATCH_YET;
  const groupKey = groupOfBucket(bucket);
  const group = MATCH_OUTCOME_GROUPS.find((g) => g.key === groupKey);
  return group?.label ?? NO_MATCH_YET;
}

interface TimingSummary {
  totalGroups: number;
  matchedGroups: number;
  noMatchGroups: number;
  medianSeconds: number | null;
  points: ChartPoint[];
}

/** The headline sentence. Under ten matched groups it gives counts instead of a median; untouched groups are always named. */
export function timingHeadline(summary: TimingSummary): string {
  const { totalGroups, matchedGroups, noMatchGroups, medianSeconds } = summary;

  if (totalGroups === 0) return "No match groups were created in this window.";

  const untouched =
    noMatchGroups === 0
      ? "Every group has at least one match."
      : `${formatNumber(noMatchGroups)} of ${formatNumber(totalGroups)} ${plural(totalGroups, "group has", "groups have")} no matches yet.`;

  if (matchedGroups === 0) {
    return `None of the ${formatNumber(totalGroups)} match ${plural(totalGroups, "group", "groups")} created in this window ${plural(totalGroups, "has", "have")} been matched yet.`;
  }

  if (matchedGroups < MIN_GROUPS_FOR_MEDIAN || medianSeconds === null) {
    return `Only ${formatNumber(matchedGroups)} of ${formatNumber(totalGroups)} match ${plural(totalGroups, "group", "groups")} ${plural(matchedGroups, "has", "have")} a first match, which is too few to give a typical time. ${untouched}`;
  }

  return `Half of match groups had their first match within ${formatGapWords(medianSeconds)}. ${untouched}`;
}

export interface MatchGroupTimingPanelProps {
  apiParams: URLSearchParams;
}

/** Section 6: how long a match group waits for its first match, and how it was made. Groups with no match
 * yet get their own bar and read "No match yet", never 0 (instant) or blank (missing). */
export function MatchGroupTimingPanel({ apiParams }: MatchGroupTimingPanelProps) {
  const apiParamsKey = apiParams.toString();
  const [rows, setRows] = useState<MatchGroupTimingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      const { data, error: apiError } = await api.GET(ENDPOINT, {
        params: { query: buildQuery(apiParams) },
      });
      if (cancelled) return;
      if (apiError || !data) {
        setError("Time to first match could not be loaded.");
      } else {
        setRows(data);
      }
      setLoading(false);
    }
    void load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiParamsKey, refreshKey]);

  const summary = useMemo<TimingSummary>(() => {
    const gaps: number[] = [];
    const counts = new Map<string, number>(TIMING_BUCKETS.map((b) => [b.key, 0]));
    let noMatchGroups = 0;

    for (const row of rows) {
      const seconds = toNullableSeconds(row.gapSeconds);
      // firstAllocationAt and gapSeconds are null together, so either one identifies an untouched group.
      if (seconds === null || !row.firstAllocationAt) {
        noMatchGroups += 1;
        continue;
      }
      gaps.push(seconds);
      const bucket = bucketOfGap(seconds);
      counts.set(bucket.key, (counts.get(bucket.key) ?? 0) + 1);
    }

    const points: ChartPoint[] = TIMING_BUCKETS.map((bucket) => ({
      key: bucket.key,
      label: bucket.label,
      groups: counts.get(bucket.key) ?? 0,
      isNoMatch: false,
    }));
    points.push({ key: "no-match", label: NO_MATCH_YET, groups: noMatchGroups, isNoMatch: true });

    return {
      totalGroups: rows.length,
      matchedGroups: gaps.length,
      noMatchGroups,
      medianSeconds: gaps.length === 0 ? null : medianOf(gaps),
      points,
    };
  }, [rows]);

  /** Newest group first: a predictable order beats a clever one. */
  const sortedRows = useMemo(
    () => [...rows].sort((a, b) => b.groupCreatedAt.localeCompare(a.groupCreatedAt)),
    [rows],
  );

  if (loading && rows.length === 0) {
    return (
      <div className="flex flex-col gap-3">
        <div className="bg-muted h-6 w-2/3 animate-pulse rounded" />
        <div className="bg-muted h-64 animate-pulse rounded-lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="border-destructive/30 bg-destructive/5 flex flex-col items-center gap-3 rounded-lg border px-6 py-10 text-center">
        <AlertTriangleIcon className="text-destructive size-6" />
        <p className="text-sm font-medium">Could not load time to first match</p>
        <p className="text-muted-foreground max-w-md text-sm">{error}</p>
        <button
          type="button"
          onClick={() => setRefreshKey((k) => k + 1)}
          className="border-border hover:bg-accent mt-1 inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm font-medium transition"
        >
          <RefreshCwIcon className="size-3.5" />
          Try again
        </button>
      </div>
    );
  }

  if (summary.totalGroups === 0) {
    return (
      <div className="border-border flex flex-col items-center gap-2 rounded-lg border border-dashed px-6 py-12 text-center">
        <InfoIcon className="text-muted-foreground size-6" />
        <p className="text-sm font-medium">No match groups in this window</p>
        <p className="text-muted-foreground max-w-md text-sm">
          Widen the date range, or check that the selected client has bills arriving.
        </p>
      </div>
    );
  }

  return (
    <div className={`${MATCH_VIZ_CLASS} flex flex-col gap-5`}>
      <style>{MATCH_VIZ_STYLES}</style>

      <div>
        <p className="text-lg leading-snug font-medium">{timingHeadline(summary)}</p>
        <p className="text-muted-foreground mt-1 text-sm">
          {formatNumber(summary.totalGroups)} match {plural(summary.totalGroups, "group", "groups")} created
          in the selected range.
        </p>
      </div>

      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={summary.points}
            margin={{ top: 4, right: 4, bottom: 0, left: -18 }}
            barCategoryGap="20%"
          >
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--color-border, #e5e7eb)" />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 10 }}
              stroke="var(--color-muted-foreground, #6b7280)"
              interval={0}
            />
            <YAxis
              tick={{ fontSize: 11 }}
              stroke="var(--color-muted-foreground, #6b7280)"
              allowDecimals={false}
            />
            <Tooltip
              {...chartTooltipProps}
              formatter={(value: number | string) => [formatNumber(toChartNumber(value)), "Match groups"]}
            />
            <Bar dataKey="groups" name="Match groups" stroke="var(--viz-surface)" strokeWidth={2}>
              {summary.points.map((point) => (
                <Cell
                  key={point.key}
                  // "No match yet" isn't a duration, so it's deliberately outside the five bars' shared colour.
                  fill={point.isNoMatch ? "var(--color-muted-foreground, #6b7280)" : "var(--series-1)"}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div>
        <div className="flex items-center gap-1.5">
          <p className="text-sm font-medium">Every match group</p>
          <InfoHint label="Every match group">
            Every group created in the selected date range, newest first, including the ones that have not
            been matched yet.
          </InfoHint>
        </div>
        <div className="mt-2 max-h-96 overflow-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Organisation</TableHead>
                <TableHead>Group created</TableHead>
                <TableHead>First match</TableHead>
                <TableHead className="text-right">Time to first match</TableHead>
                <TableHead>How the first match was made</TableHead>
                {MATCH_OUTCOME_GROUPS.map((group) => (
                  <TableHead key={group.key} className="text-right">
                    {group.label}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedRows.map((row) => {
                const seconds = toNullableSeconds(row.gapSeconds);
                const matched = seconds !== null && Boolean(row.firstAllocationAt);
                const methodCounts: Record<MatchOutcomeGroupKey, number> = {
                  Automatic: toChartNumber(row.automatic),
                  Confirmed: toChartNumber(row.suggestionApproved),
                  Manual: toChartNumber(row.manual),
                };
                return (
                  <TableRow key={row.matchGroupId}>
                    <TableCell className="max-w-48 truncate">{row.organisationName}</TableCell>
                    <TableCell className="whitespace-nowrap">{formatDateTime(row.groupCreatedAt)}</TableCell>
                    <TableCell className="whitespace-nowrap">
                      {matched ? (
                        formatDateTime(row.firstAllocationAt)
                      ) : (
                        <span className="text-muted-foreground">{NO_MATCH_YET}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap tabular-nums">
                      {matched ? (
                        formatGap(seconds)
                      ) : (
                        <span className="text-muted-foreground">{NO_MATCH_YET}</span>
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {matched ? (
                        firstMethodLabel(row.firstAllocationBucket)
                      ) : (
                        <span className="text-muted-foreground">{NO_MATCH_YET}</span>
                      )}
                    </TableCell>
                    {MATCH_OUTCOME_GROUPS.map((group) => (
                      <TableCell key={group.key} className="text-right tabular-nums">
                        {formatNumber(methodCounts[group.key])}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>

      <Accordion type="single" collapsible className="border-t">
        <AccordionItem value="details" className="border-b-0">
          <AccordionTrigger>Data details</AccordionTrigger>
          <AccordionContent className="space-y-5 text-sm">
            <p className="text-muted-foreground max-w-md">
              One row per match group, with the exact times and the gap in seconds.
            </p>

            <div>
              <p className="font-medium">The time here is wall clock, not effort</p>
              <p className="text-muted-foreground mt-1">
                It is the plain time on the calendar between the group being created and its first match. A
                group created on a Friday evening and matched on Monday morning shows the whole weekend. A
                long time here is worth reading as a calendar question before it is read as a slow queue.
              </p>
            </div>

            <div>
              <p className="font-medium">How the headline sentence is worked out</p>
              <p className="text-muted-foreground mt-1">
                It uses the median: half of the groups that have been matched were matched faster than that
                time, and half slower. Groups with no match yet are left out of it, because they have no
                time to measure, and counted separately in the same sentence. Under{" "}
                {MIN_GROUPS_FOR_MEDIAN} matched groups the section says so instead of giving a median, since
                a middle value taken from a handful of rows reads far more confident than it is.
              </p>
              <p className="mt-1 tabular-nums">
                {summary.matchedGroups === 0 || summary.medianSeconds === null
                  ? `Median not shown (${formatNumber(summary.matchedGroups)} of ${formatNumber(summary.totalGroups)} groups matched).`
                  : `Median of ${formatNumber(summary.matchedGroups)} matched ${plural(summary.matchedGroups, "group", "groups")} = ${formatGap(summary.medianSeconds)}`}
              </p>
            </div>

            <div>
              <p className="font-medium">Groups per time band</p>
              <div className="mt-1 divide-y">
                {summary.points.map((point) => (
                  <div key={point.key} className="flex items-baseline justify-between gap-4 py-1.5">
                    <span className="text-muted-foreground">{point.label}</span>
                    <span className="tabular-nums">{formatNumber(point.groups)}</span>
                  </div>
                ))}
              </div>
              <p className="text-muted-foreground mt-1 text-xs">
                Each band includes its lower time and stops just before its upper one, so every group falls
                in exactly one band.
              </p>
            </div>

            <p className="text-muted-foreground text-xs">{MATCH_SURVIVORSHIP_NOTE}</p>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
